import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, XCircle } from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

/**
 * /reset-password?token=...
 *
 * Public, unauthenticated. The token comes from the URL only — it is
 * never stored, never logged, never sent to any endpoint other than the
 * two dedicated reset endpoints.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [policy, setPolicy] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Any successful reset also fully clears any stale identity that may
  // have been cached in this browser session.
  function purgeStoredIdentity() {
    try {
      window.localStorage.removeItem("gfmp_kandake_token");
      window.localStorage.removeItem("gfmp_kandake_user");
      window.sessionStorage.clear();
    } catch { /* ignore */ }
  }

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      // Always purge any lingering identity on mount so the reset page
      // never renders in the context of a previous user's session.
      purgeStoredIdentity();
      if (!token) {
        setTokenValid(false);
        setVerifying(false);
        return;
      }
      try {
        const [verifyRes, policyRes] = await Promise.all([
          publicApi.post("/auth/reset-password/verify", { token }),
          publicApi.get("/auth/password-policy"),
        ]);
        if (cancelled) return;
        setTokenValid(!!verifyRes.data?.valid);
        setPolicy(policyRes.data || null);
      } catch {
        if (cancelled) return;
        setTokenValid(false);
      } finally {
        if (!cancelled) setVerifying(false);
      }
    }
    boot();
    return () => { cancelled = true; };
  }, [token]);

  const strengthChecks = React.useMemo(() => {
    const p = password || "";
    const min = policy?.min_length || 12;
    return [
      { key: "length",    label: `At least ${min} characters`, ok: p.length >= min },
      { key: "upper",     label: "Contains an uppercase letter", ok: /[A-Z]/.test(p) },
      { key: "lower",     label: "Contains a lowercase letter", ok: /[a-z]/.test(p) },
      { key: "number",    label: "Contains a number", ok: /[0-9]/.test(p) },
      { key: "symbol",    label: "Contains a symbol", ok: /[^A-Za-z0-9]/.test(p) },
    ];
  }, [password, policy]);

  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const allPolicyMet = strengthChecks.every((c) => c.ok);
  const canSubmit = tokenValid && !busy && passwordsMatch && allPolicyMet;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await publicApi.post("/auth/reset-password", {
        token,
        new_password: password,
        confirm_new_password: confirmPassword,
      });
      purgeStoredIdentity();
      toast.success("Password reset successful. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      const msg = errMsg(err, "Unable to reset password. Please request a new link.");
      toast.error(typeof msg === "string" ? msg : "Unable to reset password.");
    } finally {
      setBusy(false);
    }
  }

  const min = policy?.min_length || 12;

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="brand-strip text-white p-10 md:p-14 hidden md:flex flex-col justify-between relative overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -bottom-32 opacity-[0.10]" aria-hidden="true">
          <BrandLogo variant="kandake-gold" size="2xl" />
        </div>
        <div className="relative flex items-center gap-3">
          <BrandLogo variant="gfmp" size="sm" withRing />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">Operating authority</div>
            <div className="text-sm font-medium leading-tight">Good Friar Music Publishing</div>
          </div>
        </div>
        <div className="relative flex flex-col items-start">
          <BrandLogo variant="kandake-gold" size="2xl" frame="square" className="-ml-2 mb-2" />
          <div className="font-display text-[44px] leading-[1.05] tracking-tight" style={{ color: "hsl(40 48% 80%)" }}>
            Kandake Prime
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] opacity-80 mt-1">
            Internal Alpha · GFMP Command Center
          </div>
        </div>
        <div className="relative text-sm opacity-80 max-w-md">
          <span className="block font-medium text-white/95">GFMP / Durban 49 internal access only.</span>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-paper">
        <Card className="w-full max-w-md shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium"
                    style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
                    data-testid="reset-internal-alpha-badge">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(40 48% 45%)" }} /> Internal Alpha
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Lock className="w-3 h-3" /> Internal use only
              </span>
            </div>
            <h1 className="font-display text-3xl text-foreground" data-testid="reset-password-title">Set a new password</h1>

            {verifying && (
              <div className="mt-6 text-sm text-muted-foreground" data-testid="reset-password-verifying">
                Verifying reset link…
              </div>
            )}

            {!verifying && !tokenValid && (
              <div
                className="mt-6 rounded-lg border p-4 flex gap-3"
                style={{ background: "hsl(0 60% 96%)", borderColor: "hsl(0 60% 78%)" }}
                data-testid="reset-password-invalid"
                role="alert"
              >
                <XCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "hsl(347 72% 22%)" }} />
                <div className="text-sm leading-relaxed">
                  This reset link is no longer valid. It may have expired, already been used,
                  or been replaced by a newer request. Please
                  <Link to="/forgot-password" className="underline mx-1" data-testid="reset-password-request-new">
                    request a new reset link
                  </Link>
                  and try again.
                </div>
              </div>
            )}

            {!verifying && tokenValid && (
              <>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a strong password. You will be signed out of any active session and
                  must sign back in with your new password.
                </p>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
                  <div>
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={min}
                      data-testid="reset-password-new-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={min}
                      data-testid="reset-password-confirm-input"
                    />
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <div className="text-[12px] text-destructive mt-1" data-testid="reset-password-mismatch">
                        Passwords do not match.
                      </div>
                    )}
                  </div>

                  <ul className="text-[12.5px] space-y-1" data-testid="reset-password-policy-list">
                    {strengthChecks.map((c) => (
                      <li key={c.key} className="flex items-center gap-2">
                        <ShieldCheck
                          className="w-3.5 h-3.5"
                          style={{ color: c.ok ? "hsl(140 55% 32%)" : "hsl(0 0% 65%)" }}
                          aria-hidden="true"
                        />
                        <span className={c.ok ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!canSubmit}
                    data-testid="reset-password-submit-button"
                    style={{ background: "hsl(347 72% 22%)" }}
                  >
                    {busy ? "Setting new password…" : "Set new password"}
                  </Button>
                </form>
              </>
            )}

            <div className="text-center mt-6 text-[12px]">
              <Link
                to="/login"
                className="text-muted-foreground hover:text-foreground underline"
                data-testid="reset-password-back-to-login"
              >
                ← Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
